"use client"
import EditorWrapper from "@/components/Editor";
import {Button, Modal, ModalContent, ModalHeader, ModalBody} from "@nextui-org/react";
import {copyToClipboard, saveText} from "@/lib/utils";
import React, {FC, useEffect, useState} from "react";
import {editor} from "monaco-editor";
import ReactDiffViewer from "react-diff-viewer-continued";
import {CodeFormatter, HandleJSONChanges, HandleXMLChanges, HandleYAMLChanges} from "@/lib/editor";
import {ConversionDataTypes} from "@/state/features/types";
import {Camera, Check, Copy, X} from "lucide-react";
import ResizableSplit from "@/components/ResizableSplit";
import EditorToolbar from "@/components/Editor/EditorToolbar";
import {JSONMinifier} from "@/lib/json";
import {ToolName, trackToolAction} from "@/lib/analytics";
import {canonicalizeForDiff, StructuralFormat} from "@/lib/canonicalize";
import {useTheme} from "@/app/ThemeProvider";

interface ValidatorProps{
    language: ConversionDataTypes
    startingValue: string;
}

const Validator: FC<ValidatorProps> = (props: ValidatorProps) => {
    const {language, startingValue} = props;
    // The enum's values are the tool names ("json", "xml", "yaml"); the template literal turns the enum into that string union.
    const toolName: ToolName = `${language}`;
    const [showValidations, setShowValidations] = useState<boolean>(false)
    const [code, setCode] = useState<string>(startingValue);
    const [validationErrors, setValidationErrors] = useState<Array<editor.IMarker>>([]);
    const [copied, setCopied] = useState<boolean>(false);


    const [monacoEditor, setMonacoEditor] = useState<null | typeof editor>(null);
    const [editorInstance, setEditorInstance] = useState<null | editor.IStandaloneCodeEditor>(null);
    const [snapshot, setSnapshot] = useState<string | null>(null);
    const [canonicalSnapshot, setCanonicalSnapshot] = useState('');
    const [canonicalCode, setCanonicalCode] = useState('');
    const [snapshotError, setSnapshotError] = useState<string | undefined>(undefined);
    const [showSnapshotDiff, setShowSnapshotDiff] = useState<boolean>(false);
    const { theme } = useTheme();

    const validationHandler = () => {
        trackToolAction(toolName, "validate");
        if (!!editorInstance && !!monacoEditor) {
            if(language === "json"){
                HandleJSONChanges(code, monacoEditor, editorInstance)
            }
            else if(language === "xml") {
                HandleXMLChanges(code, monacoEditor, editorInstance)
            }
            else if(language === "yaml"){
                HandleYAMLChanges(code, monacoEditor, editorInstance)
            }
            const errors = monacoEditor.getModelMarkers({owner: language})
            setValidationErrors(errors);
            setShowValidations(true);
        }
    }
    const formatHandler = () => {
        trackToolAction(toolName, "format");
        if(!!editorInstance && !!monacoEditor) {
            if(language === "json") {
                HandleJSONChanges(code, monacoEditor, editorInstance)
            }
            else if(language === "xml") {
                HandleXMLChanges(code, monacoEditor, editorInstance)
            }
            else if(language === "yaml"){
                HandleYAMLChanges(code, monacoEditor, editorInstance)
            }
            const errors = monacoEditor.getModelMarkers({owner: language});
            if(errors.length > 0){
                setValidationErrors(errors);
                setShowValidations(true)
                return;
            }
            // if there are no issue then format code
            setCode(CodeFormatter(code, language))

        }
    }

    /** JSON only: checks the text first, then removes all whitespace outside strings. Invalid JSON shows its errors instead. */
    const minifyHandler = () => {
        trackToolAction(toolName, "minify");
        if(!!editorInstance && !!monacoEditor) {
            HandleJSONChanges(code, monacoEditor, editorInstance)
            const errors = monacoEditor.getModelMarkers({owner: language});
            if(errors.length > 0){
                setValidationErrors(errors);
                setShowValidations(true)
                return;
            }
            setCode(JSONMinifier(code))
        }
    }

    function handleEditorChange(value: string | undefined) {
        trackToolAction(toolName, "edit");
        if(!!value){
            setCode(value)
            setShowValidations(false);
        }
    }

    const handleValidationClick = (lineNumber: number, column: number) => {
        if(editorInstance) {
            editorInstance.setPosition({lineNumber, column});
            editorInstance.revealLineInCenter(lineNumber);
            editorInstance.focus();
        }
    }

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                formatHandler();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    });

    const handleCopy = async () => {
        if (code) {
            trackToolAction(toolName, "copy");
            await copyToClipboard(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Canonicalize the snapshot and current code (sorted keys, consistent
    // formatting) whenever either changes, so the diff ignores formatting-only edits
    useEffect(() => {
        if (snapshot === null) return;
        let cancelled = false;
        (async () => {
            const [before, after] = await Promise.all([
                canonicalizeForDiff(snapshot, language as StructuralFormat),
                canonicalizeForDiff(code, language as StructuralFormat),
            ]);
            if (cancelled) return;
            setSnapshotError(after.error || before.error);
            setCanonicalSnapshot(before.text ?? '');
            setCanonicalCode(after.text ?? '');
        })();
        return () => {
            cancelled = true;
        };
    }, [snapshot, code, language]);

    const hasSnapshotChanges = !snapshotError && canonicalSnapshot !== canonicalCode;

    const handleSnapshot = () => {
        trackToolAction(toolName, "snapshot");
        setSnapshot(code);
    };

    const clearSnapshot = () => {
        setSnapshot(null);
        setCanonicalSnapshot('');
        setCanonicalCode('');
        setSnapshotError(undefined);
    };

    const editorElement = (
        <EditorWrapper value={code}
                       language={language}
                       setEditor={setEditorInstance}
                       setMonacoEditor={setMonacoEditor}
                       changeHandler={handleEditorChange}
                       showValidationPanel={showValidations}
                       isValid={validationErrors.length === 0}
        />
    );

    const validationPanel = (
        <div className="w-full flex flex-col h-full border-1 dark:border-gray-700">
            <p className="text-center py-5 border-b-1 dark:border-gray-700 dark:text-gray-100">Validation Errors</p>
            {validationErrors.map((error) => {
                return (
                    <div
                        key={`${error.startColumn}${error.endColumn} - ${error.startLineNumber}${error.endLineNumber}`}
                        onClick={(e) => {
                            e.preventDefault();
                            handleValidationClick(error.startLineNumber, error.startColumn)
                        }}
                        className="w-full group flex items-center border-b-1 dark:border-gray-700 hover:cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"
                             strokeWidth={1.5} stroke="currentColor"
                             className="size-8 text-gray-500 dark:text-gray-400 group-hover:text-blue-900 dark:group-hover:text-blue-400 shrink-0">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/>
                        </svg>
                        <div className="py-4 text-sm">
                            <p className="text-red-500">{error.message}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Line {error.startLineNumber}, column {error.startColumn}</p>
                        </div>
                    </div>
                )
            })}
        </div>
    );

    const showPanel = showValidations && validationErrors.length > 0;

    return (
        <div className="w-full flex flex-col items-center">
            <div className="w-full flex justify-end">
                <EditorToolbar />
            </div>
            <div className="w-full">
                {showPanel ? (
                    <ResizableSplit
                        left={editorElement}
                        right={validationPanel}
                        defaultPercent={75}
                        minPercent={50}
                        maxPercent={85}
                        storageKey={`split-validator-${language}`}
                        leftLabel="editor"
                        rightLabel="validation errors"
                    />
                ) : (
                    editorElement
                )}
            </div>

            <div className="flex flex-wrap gap-3 my-5 w-full">
                <Button color="primary" variant="bordered" radius="sm" onClick={validationHandler}>
                    Validate
                </Button>

                <Button color="primary" variant="bordered" radius="sm" onClick={formatHandler} title="Ctrl/Cmd + Enter">
                    Format
                </Button>

                {language === "json" && (
                    <Button color="primary" variant="bordered" radius="sm" onClick={minifyHandler}>
                        Minify
                    </Button>
                )}

                <Button
                    color="primary"
                    variant="bordered"
                    radius="sm"
                    onClick={handleCopy}
                    startContent={copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                >
                    {copied ? "Copied!" : "Copy"}
                </Button>

                <Button color="primary" variant="bordered" radius="sm" onClick={() => {
                    if(code){
                        trackToolAction(toolName, "download");
                        saveText(code, language);
                    }
                }}>Download
                </Button>

                {snapshot === null ? (
                    <Button
                        color="secondary"
                        variant="bordered"
                        radius="sm"
                        startContent={<Camera className="w-4 h-4" aria-hidden="true" />}
                        onClick={handleSnapshot}
                        title="Save a snapshot to compare later edits against"
                    >
                        Snapshot
                    </Button>
                ) : (
                    <div className="flex items-center gap-2">
                        <Button
                            color={hasSnapshotChanges ? "warning" : "secondary"}
                            variant="flat"
                            radius="sm"
                            onClick={() => setShowSnapshotDiff(true)}
                        >
                            {snapshotError
                                ? "Changes since snapshot"
                                : hasSnapshotChanges
                                    ? "Changes since snapshot"
                                    : "No changes since snapshot"}
                        </Button>
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            radius="full"
                            aria-label="Clear snapshot"
                            title="Clear snapshot"
                            onClick={clearSnapshot}
                        >
                            <X className="w-4 h-4" aria-hidden="true" />
                        </Button>
                    </div>
                )}
            </div>

            <Modal isOpen={showSnapshotDiff} onClose={() => setShowSnapshotDiff(false)} size="3xl" scrollBehavior="inside">
                <ModalContent>
                    <ModalHeader>Changes since snapshot</ModalHeader>
                    <ModalBody className="pb-6">
                        {snapshotError ? (
                            <div className="p-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg">
                                <p className="font-medium mb-1">Current content is not valid {language.toUpperCase()}</p>
                                <p className="text-sm">{snapshotError}</p>
                            </div>
                        ) : !hasSnapshotChanges ? (
                            <div className="p-8 bg-green-50 dark:bg-green-950 rounded-lg text-center">
                                <p className="text-green-600 dark:text-green-400 text-lg font-semibold">No changes since snapshot</p>
                            </div>
                        ) : (
                            <ReactDiffViewer
                                oldValue={canonicalSnapshot}
                                newValue={canonicalCode}
                                splitView={false}
                                useDarkTheme={theme === "dark"}
                                leftTitle="Snapshot"
                                rightTitle="Current"
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    )
}

export default Validator;
